import {
  Card,
  CardContent,
  CardHeader,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@altimateai/lego";
import { CodeSQLEditor } from "@/components/codeEditor";
import { Spinner, Stack } from "@altimateai/lego";
import { TIME_PERIODS } from "@/pages/warehouse/constants";
import { CommonTaskExecutionSummary } from "@/pages/query/common/tasks-details/CommonTaskExecutionSummary";
import { CommonTaskRunsGraph } from "@/pages/query/common/tasks-details/CommonTaskRunsGraph";
import { CommonTaskRunsTable } from "@/pages/query/common/tasks-details/CommonTaskRunsTable";
import { ExecutionStatus } from "@/pages/query/constants";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import {
  getDbtJobRunsList,
  getDbtJobRunsListGraphData,
  getDbtModelRunSummary,
  getQueryTextByHash,
} from "@/helpers/apis";
import { useParams } from "react-router-dom";
import { BackButton } from "@/components/button";
import { QueryTextResponse } from "@/pages/query/types";
import { QUERY_TEXT_API_KEY } from "@/pages/query/constants";
import {
  getDateByStartOfDayWithoutTimeoffset,
  getDateByEndOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { InsightSection } from "@/modules/insights";
import { useDateFromSearchParams, useResourceOpportunities } from "@/helpers/useTimeHooks";
import { ResourceType } from "@/pages/explorer/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useEffect } from "react";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const TasksDetails = () => {
  const { taskId = "" } = useParams();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { setTotalPages, setTotalItems } = useTablePagination();
  const { startDate, endDate, onUpdate: onDateRangeChange } = useDateFromSearchParams();

  // Extract search params to variables
  const timePeriod = parseInt(
    searchParams.get("timePeriod") || TIME_PERIODS[2].period.toString(),
    10
  );
  const sortAttribute = searchParams.get("sortAttribute") || "run_id";
  const sortOrder = (searchParams.get("sortOrder") as "asc" | "desc") || "desc";
  const executionStatus =
    searchParams.get("executionStatus") || `${ExecutionStatus.SUCCESS},${ExecutionStatus.FAIL}`;
  const executionStatusFilter = executionStatus.split(",");
  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "20", 10);
  const queryHash = searchParams.get("queryHash") || "";
  const workflowId = searchParams.get("workflowId") || "";

  const workflowName = workflowId ? decodeURIComponent(workflowId) : "";

  useBreadcrumb(taskId ? decodeURIComponent(taskId) : "Task Details", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Workloads", pathname: "/query/snowflake-jobs" },
      ...(workflowName
        ? [
            {
              label: workflowName,
              pathname: `/query/snowflake-jobs/${encodeURIComponent(workflowId)}`,
            },
          ]
        : []),
    ],
  });
  const selectedOpportunityId = searchParams.get("opportunityId");
  const setSelectedOpportunityId = (v: string) => updateSearchParamsByKey({ opportunityId: v });

  const getFilterParams = () => {
    return {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: sortOrder,
      execution_status: executionStatusFilter,
      task_id: taskId,
      startDate: getDateByStartOfDayWithoutTimeoffset(
        dayjs().subtract(timePeriod, "day").toDate()
      ).toISOString(),
      endDate: getDateByEndOfDayWithoutTimeoffset(dayjs().toDate())!.toISOString(),
    };
  };

  const { data: opportunities, isFetching: isLoadingOpportunities } = useResourceOpportunities({
    resourceUri: queryHash,
    resourceType: ResourceType.QueryHash,
  });

  const { data: queryText, isFetching: isFetchingQueryText } = useQuery<QueryTextResponse>({
    queryKey: [QUERY_TEXT_API_KEY, queryHash],
    queryFn: async () => {
      const response = await getQueryTextByHash(queryHash);
      if (!response) {
        throw new Error("Failed to fetch query text");
      }
      return response;
    },
    enabled: !!queryHash,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const { data, isFetching } = useQuery({
    queryKey: [
      "task-details",
      timePeriod,
      sortAttribute,
      sortOrder,
      executionStatus,
      page,
      pageSize,
    ],
    queryFn: () => getDbtJobRunsList(taskId, getFilterParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      setTotalPages(data?.pages ?? 0);
      setTotalItems(data?.total ?? 0);
    }
  }, [data, setTotalPages, setTotalItems]);

  const { data: graphData, isLoading: isGraphDataLoading } = useQuery({
    queryKey: ["task-details-graph-data", timePeriod, sortOrder, executionStatus],
    queryFn: () => {
      return getDbtJobRunsListGraphData(taskId, {
        sort_key: "start_time",
        sort_order: "asc",
        execution_status: executionStatusFilter,
        task_id: taskId,
        startDate: getDateByStartOfDayWithoutTimeoffset(
          dayjs().subtract(timePeriod, "day").toDate()
        ).toISOString(),
        endDate: getDateByEndOfDayWithoutTimeoffset(dayjs().toDate())!.toISOString(),
      });
    },
  });

  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["dbt-summary", timePeriod],
    queryFn: () =>
      getDbtModelRunSummary(taskId, {
        task_id: taskId,
        startDate: getDateByStartOfDayWithoutTimeoffset(
          dayjs().subtract(timePeriod, "day").toDate()
        ).toISOString(),
        endDate: getDateByEndOfDayWithoutTimeoffset(dayjs().toDate())!.toISOString(),
      }),
  });

  const handleChangeTimePeriod = (period: string) => {
    updateSearchParamsByKey({ timePeriod: period });
  };

  return (
    <Card>
      <CardHeader>
        <Stack align={"center"}>
          <BackButton defaultUrl={`/query/snowflake-jobs/${workflowId}`} />
          <Typography variant={"section-heading"}>{taskId}</Typography>
        </Stack>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={"code"} variant={"underlined"}>
          <TabsList>
            <TabsTrigger value="code">Code</TabsTrigger>
            <TabsTrigger value="runs">Runs</TabsTrigger>
            <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
          </TabsList>
          <TabsContent value="code">
            {isFetchingQueryText ? (
              <Stack justify={"center"} align={"center"} className={"al-h-40 al-w-full"}>
                <Spinner />
                Loading query...
              </Stack>
            ) : (
              <CodeSQLEditor
                value={queryText?.query_text || ""}
                height="36vh"
                lineNumbers={true}
                showCopy={true}
                minimap
              />
            )}
          </TabsContent>
          <TabsContent value="runs">
            <Stack direction={"column"} gap={"xl"} className={"al-p-1"}>
              <Stack gap={"lg"} className={"al-flex-row-reverse"}>
                <Stack direction={"row"} align={"center"} gap={"md"}>
                  <Label className={"al-m-0"}>Time Period:</Label>
                  <Select
                    onValueChange={handleChangeTimePeriod}
                    defaultValue={timePeriod.toString()}
                  >
                    <SelectTrigger className="al-w-[150px]">
                      <SelectValue placeholder="Select a time period" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_PERIODS.map(period => {
                        return (
                          <SelectItem key={period.label} value={period.period.toString()}>
                            {period.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </Stack>
              </Stack>
              <CommonTaskExecutionSummary summary={summary} isLoading={isSummaryLoading} />
              <Stack direction="column" className={"al-p-4 al-border al-rounded-md"}>
                <CommonTaskRunsGraph graphData={graphData} isLoading={isGraphDataLoading} />
              </Stack>
              <Stack direction={"column"} gap={"md"} className={"al-mt-4"}>
                <CommonTaskRunsTable items={data?.items ?? []} isLoading={isFetching} />
              </Stack>
            </Stack>
          </TabsContent>
          <TabsContent value="opportunities">
            {isLoadingOpportunities ? (
              <Stack justify={"center"} align={"center"} className={"al-h-40 al-w-full"}>
                <Spinner />
                Loading opportunities...
              </Stack>
            ) : (
              <InsightSection
                opportunities={opportunities?.data ?? []}
                totalRuns={""}
                selectedOpportunityId={selectedOpportunityId}
                setOpportunityId={setSelectedOpportunityId}
                startDate={startDate}
                endDate={endDate}
                onDateRangeChange={onDateRangeChange}
              />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
